import type { Column, RowData } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { TableColumnResizeHandle } from "./TableColumnResizeHandle";
import { useTableContext } from "./TableContext";
import type { TableColumn } from "./types";

export interface TableHeaderCellProps<TRow extends RowData> {
  column: Column<TRow, unknown>;
}

const ALIGN_CLASS: Record<NonNullable<TableColumn<unknown>["align"]>, string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
};

export const TableHeaderCell = <TRow extends RowData>({ column }: TableHeaderCellProps<TRow>) => {
  const { t } = useTranslation();
  const { layoutById, shares, getResizeHandleProps, resizingColumnId } = useTableContext();
  const layout = layoutById[column.id];
  // A column with no label (an actions column) shows nothing; its id still names it for assistive tech.
  const visibleLabel = layout?.label ?? "";
  const label = layout?.label ?? column.id;
  const isSorted = column.getIsSorted();
  const handleProps = getResizeHandleProps(column.id);

  const cycleSorting = () => {
    if (!isSorted) column.toggleSorting(false);
    else if (isSorted === "asc") column.toggleSorting(true);
    else column.clearSorting();
  };

  return (
    <div
      data-slot="table-header-cell"
      data-testid={`column-header-${column.id}`}
      data-column-id={column.id}
      className={cn("relative flex min-w-0 items-center px-2", ALIGN_CLASS[layout?.align ?? "left"])}
    >
      {/* The clip sits on this wrapper, not the cell, because the resize handle straddles the cell's edge. */}
      <div className={cn("flex min-w-0 overflow-hidden", ALIGN_CLASS[layout?.align ?? "left"])}>
        {column.getCanSort() ? (
          <button
            type="button"
            onClick={cycleSorting}
            aria-label={t("Sort by {{column}}", { column: label })}
            // Preflight resets `text-transform` on buttons, which would exempt sortable labels from the
            // header's `structuralRowsText` treatment.
            className="flex min-w-0 items-center gap-1 rounded-sm outline-none [text-transform:inherit] hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <span className="truncate">{visibleLabel}</span>
            {isSorted === "asc" ? (
              <ArrowUp data-testid="sort-icon-asc" className="size-4 shrink-0" />
            ) : isSorted === "desc" ? (
              <ArrowDown data-testid="sort-icon-desc" className="size-4 shrink-0" />
            ) : (
              <ArrowUpDown className="size-4 shrink-0 opacity-40" />
            )}
          </button>
        ) : (
          <span className="truncate">{visibleLabel}</span>
        )}
      </div>
      {handleProps && (
        <TableColumnResizeHandle
          label={label}
          share={shares[column.id] ?? 0}
          isResizing={resizingColumnId === column.id}
          {...handleProps}
        />
      )}
    </div>
  );
};
